import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Container,
  Card,
  CardContent,
  Alert,
  styled,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  useResetPassword,
  useValidateResetToken
} from '../../../../hooks/usePasswordReset';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const ContentWrapper = styled(Box)(
  () => `
    height: 100%;
    display: flex;
    flex: 1;
    overflow: auto;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`
);

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [resetComplete, setResetComplete] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validateTokenMutation = useValidateResetToken();
  const resetPasswordMutation = useResetPassword();

  useEffect(() => {
    // Extract token from URL query parameters
    const queryParams = new URLSearchParams(location.search);
    const tokenParam = queryParams.get('token');

    if (tokenParam) {
      setToken(tokenParam);
      // Validate the token when it's available
      validateTokenMutation.mutate(tokenParam, {
        onError: (error: Error) => {
          let message = 'Invalid or expired token';
          // Check if it has the expected Axios-like structure
          if (error && typeof error === 'object' && 'response' in error) {
            const apiError = error as {
              response?: { data?: { message?: string } };
            };
            message = apiError.response?.data?.message || message;
          }
          setError(message);
        }
      });
    } else {
      setError('Invalid or missing reset token');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- validateTokenMutation causes infinite loop if included
  }, [location.search]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    try {
      await resetPasswordMutation.mutateAsync({
        token,
        password,
        confirmPassword
      });
      setResetComplete(true);
    } catch (error: unknown) {
      console.error('Error resetting password:', error);
      let message = 'Failed to reset password. Please try again.';
      // Type check for the expected structure
      if (
        error &&
        typeof error === 'object' &&
        'response' in error &&
        error.response &&
        typeof error.response === 'object' &&
        'data' in error.response &&
        error.response.data &&
        typeof error.response.data === 'object' &&
        'message' in error.response.data &&
        typeof error.response.data.message === 'string'
      ) {
        message = error.response.data.message;
      }
      setError(message);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <ContentWrapper>
      <Container maxWidth="sm">
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3
              }}
            >
              <Typography variant="h3" component="h1">
                Reset Your Password
              </Typography>

              {error && (
                <Alert severity="error" sx={{ width: '100%' }}>
                  {error}
                </Alert>
              )}

              {resetComplete ? (
                <>
                  <Alert severity="success" sx={{ width: '100%' }}>
                    Your password has been successfully reset.
                  </Alert>
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    You can now log in with your new password.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate('/login')}
                    sx={{ mt: 2 }}
                  >
                    Go to Login
                  </Button>
                </>
              ) : (
                <>
                  <Typography
                    variant="body1"
                    sx={{ textAlign: 'center', mb: 2 }}
                  >
                    Please enter your new password below.
                  </Typography>

                  <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{ width: '100%' }}
                  >
                    <TextField
                      fullWidth
                      label="New Password"
                      type={showPassword ? 'text' : 'password'}
                      variant="outlined"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      margin="normal"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleTogglePasswordVisibility}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />

                    <TextField
                      fullWidth
                      label="Confirm New Password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      variant="outlined"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      margin="normal"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleToggleConfirmPasswordVisibility}
                              edge="end"
                            >
                              {showConfirmPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      color="primary"
                      size="large"
                      sx={{ mt: 3 }}
                      disabled={resetPasswordMutation.isPending || !token}
                    >
                      {resetPasswordMutation.isPending
                        ? 'Resetting...'
                        : 'Reset Password'}
                    </Button>
                  </Box>
                </>
              )}

              {!resetComplete && (
                <Box sx={{ mt: 2 }}>
                  <Link to="/login">
                    <Button variant="text">Back to Login</Button>
                  </Link>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Container>
    </ContentWrapper>
  );
};

export default ResetPasswordPage;
